import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { z } from 'zod'

import { PaymentRails } from '@/domains/Business/constants'
import { parseFullPhoneNumber } from '@/lib/phone'
import { removeEmptyFormFields } from '@/lib/utils'
import { Button, Form, StickyContainer } from '@/shared/ui'

import { CreatedRecipient, EntityType } from '../types'

import { ContactDetailsFields } from './ContactDetailsFields'
import { DeleteRecipientAction } from './DeleteRecipientAction'
import { EUPaymentFields } from './EUPaymentFields'
import { RecipientDetailsFields } from './RecipientDetailsFields'
import { ibanValidation, recipientValidation } from './validations'

const sepaRecipientValidation = recipientValidation.and(ibanValidation)

type SEPARecipientFormSchema = z.infer<typeof sepaRecipientValidation>

const SEPA_FORM_ID = 'sepa-recipient-form'

type Props = {
  recipient?: CreatedRecipient
  onContinue: (recipient: SEPARecipientFormSchema) => void
  layout?: 'edit' | 'create'
  isPending?: boolean
  recipientId?: string
  onDelete?: () => void
}

export const EURecipientForm = ({
  recipient,
  onContinue,
  layout = 'create',
  isPending,
  recipientId,
  onDelete,
}: Props) => {
  const getFormValues = () => {
    switch (recipient?.paymentInformation.rails) {
      case PaymentRails.EU:
        switch (recipient?.recipient.type) {
          case EntityType.INDIVIDUAL:
            return {
              entityType: recipient.recipient.type,
              nickname: recipient.nickname,
              firstName: recipient.recipient.details.firstName,
              lastName: recipient.recipient.details.lastName,
              secondLastName: recipient.recipient.details.secondLastName,
              email: recipient.contactDetails?.email,
              localPhoneNumber:
                recipient.contactDetails?.internationalPhonePrefix &&
                recipient.contactDetails?.localPhoneNumber
                  ? recipient.contactDetails.internationalPhonePrefix +
                    recipient.contactDetails.localPhoneNumber
                  : '',
              internationalPhonePrefix:
                recipient.contactDetails?.internationalPhonePrefix,

              iban: recipient.paymentInformation.railsDetails.iban,
            }

          case EntityType.BUSINESS:
            return {
              entityType: recipient.recipient.type,
              nickname: recipient.nickname,
              legalName: recipient.recipient.details.legalName,
              email: recipient.contactDetails?.email,
              localPhoneNumber:
                recipient.contactDetails?.internationalPhonePrefix &&
                recipient.contactDetails?.localPhoneNumber
                  ? recipient.contactDetails.internationalPhonePrefix +
                    recipient.contactDetails.localPhoneNumber
                  : '',
              internationalPhonePrefix:
                recipient.contactDetails?.internationalPhonePrefix,

              iban: recipient.paymentInformation.railsDetails.iban,
            }

          default:
            return undefined
        }

      default:
        return {
          entityType: '' as EntityType,
          nickname: '',
          legalName: '',
          firstName: '',
          lastName: '',
          secondLastName: '',
          email: '',
          localPhoneNumber: '',
          internationalPhonePrefix: '+49',
          iban: '',
        }
    }
  }

  const form = useForm<SEPARecipientFormSchema>({
    mode: 'onChange',
    resolver: zodResolver(sepaRecipientValidation),
    values: getFormValues(),
  })

  const onSubmit = (values: SEPARecipientFormSchema) => {
    removeEmptyFormFields(values)

    onContinue({
      ...values,
      localPhoneNumber: parseFullPhoneNumber(values),
    })
  }

  return (
    <>
      <Form {...form}>
        <form
          id={SEPA_FORM_ID}
          className="w-full"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-8">
            <RecipientDetailsFields layout={layout} form={form} />
            <ContactDetailsFields form={form} />
            <EUPaymentFields layout={layout} form={form} />
          </div>
        </form>
      </Form>

      <StickyContainer
        className={layout === 'edit' ? '-bottom-16 pb-0' : undefined}
      >
        <Button
          width="full"
          form={SEPA_FORM_ID}
          disabled={
            !form.formState.isValid ||
            isPending ||
            (layout === 'edit' && !form.formState.isDirty)
          }
          loading={isPending}
          onClick={form.handleSubmit(onSubmit)}
          type="submit"
        >
          {layout === 'edit' ? (
            <FormattedMessage
              defaultMessage="Save changes"
              id="action.saveChanges"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Save & Continue"
              id="action.saveAndContinue"
            />
          )}
        </Button>

        {onDelete ? (
          <DeleteRecipientAction
            onDelete={onDelete}
            recipient={recipient}
            recipientId={recipientId}
          />
        ) : null}
      </StickyContainer>
    </>
  )
}
